import { useCallback, type Dispatch, type SetStateAction } from 'react';
import { useDispatch } from 'react-redux';
import type { QuoteStep, ScheduleOverrides } from '../types/quote.types';
import type { ConfiguredService, ConfiguredAddOn } from '@features/service-plans/types/services.types';
import type { SmriqConfirmedVehicle, SmriqPlanSettings } from '@features/service-plans/types/smriq.types';
import {
    setConfiguredServices,
    setConfiguredAddOns,
    dismissAutoAddOn,
    applyAutoAddOnBaseline,
    setResolvedPlanId,
    setServicePlanSessionId,
    setSmriqConfirmedVehicle,
    syncSmriqConfirmedMileage,
    setSmriqPlanSettings,
    setScheduleOverrides,
    applyDerivedSchedule,
    resetFromStep,
} from '../store/quoteSlice';

interface UseQuoteConfigurationActionsArgs {
    readonly configuredServices: ConfiguredService[];
    readonly configuredAddOns: ConfiguredAddOn[];
    readonly stepOrder: readonly QuoteStep[];
}

export interface QuoteConfigurationActions {
    updateServices: Dispatch<SetStateAction<ConfiguredService[]>>;
    updateAddOns: Dispatch<SetStateAction<ConfiguredAddOn[]>>;
    dismissAutoAddOn: (addOnId: string) => void;
    applyAutoAddOnBaseline: (addOnIds: string[]) => void;
    // Stable identity so consumers can list it in effect dependency arrays without churn.
    setResolvedPlanId: (planId: string | null) => void;
    setServicePlanSessionId: (sessionId: string | null) => void;
    setSmriqConfirmedVehicle: (vehicle: SmriqConfirmedVehicle | null) => void;
    // Writes the vendor-confirmed mileages onto the quote vehicle WITHOUT invalidating the
    // session that was just confirmed against them; `selectVehicle` would clear it.
    syncSmriqConfirmedMileage: (mileages: { mileage: number; annualMileage: number }) => void;
    setSmriqPlanSettings: (settings: SmriqPlanSettings | null) => void;
    setScheduleOverrides: (overrides: ScheduleOverrides | undefined) => void;
    // One dispatch writes both lists, so a linked add-on can never be
    // observed against a service row list it was not re-keyed for (D11).
    applyDerivedSchedule: (payload: {
        services: ConfiguredService[];
        addOns: ConfiguredAddOn[];
    }) => void;
    // D14: any override change clears a confirmed payment selection.
    clearPaymentSelection: () => void;
}

export const useQuoteConfigurationActions = ({
    configuredServices,
    configuredAddOns,
    stepOrder,
}: UseQuoteConfigurationActionsArgs): QuoteConfigurationActions => {
    const dispatch = useDispatch();

    const updateServices: Dispatch<SetStateAction<ConfiguredService[]>> = useCallback(
        (servicesOrUpdater) => {
            if (typeof servicesOrUpdater === 'function') {
                const newServices = servicesOrUpdater(configuredServices);
                dispatch(setConfiguredServices(newServices));
            } else {
                dispatch(setConfiguredServices(servicesOrUpdater));
            }
        },
        [dispatch, configuredServices]
    );

    const updateAddOns: Dispatch<SetStateAction<ConfiguredAddOn[]>> = useCallback(
        (addOnsOrUpdater) => {
            if (typeof addOnsOrUpdater === 'function') {
                const newAddOns = addOnsOrUpdater(configuredAddOns);
                dispatch(setConfiguredAddOns(newAddOns));
            } else {
                dispatch(setConfiguredAddOns(addOnsOrUpdater));
            }
        },
        [dispatch, configuredAddOns]
    );

    const dismissAutoAddOnAction = useCallback(
        (addOnId: string) => dispatch(dismissAutoAddOn(addOnId)),
        [dispatch]
    );

    const applyAutoAddOnBaselineAction = useCallback(
        (addOnIds: string[]) => dispatch(applyAutoAddOnBaseline(addOnIds)),
        [dispatch]
    );

    const setResolvedPlanIdAction = useCallback(
        (planId: string | null) => dispatch(setResolvedPlanId(planId)),
        [dispatch]
    );

    const setServicePlanSessionIdAction = useCallback(
        (sessionId: string | null) => dispatch(setServicePlanSessionId(sessionId)),
        [dispatch]
    );

    const setSmriqConfirmedVehicleAction = useCallback(
        (vehicle: SmriqConfirmedVehicle | null) => dispatch(setSmriqConfirmedVehicle(vehicle)),
        [dispatch]
    );

    const syncSmriqConfirmedMileageAction = useCallback(
        (mileages: { mileage: number; annualMileage: number }) =>
            dispatch(syncSmriqConfirmedMileage(mileages)),
        [dispatch]
    );

    const setSmriqPlanSettingsAction = useCallback(
        (settings: SmriqPlanSettings | null) => dispatch(setSmriqPlanSettings(settings)),
        [dispatch]
    );

    const setScheduleOverridesAction = useCallback(
        (overrides: ScheduleOverrides | undefined) => dispatch(setScheduleOverrides(overrides)),
        [dispatch]
    );

    const applyDerivedScheduleAction = useCallback(
        (payload: { services: ConfiguredService[]; addOns: ConfiguredAddOn[] }) =>
            dispatch(applyDerivedSchedule(payload)),
        [dispatch]
    );

    const clearPaymentSelectionAction = useCallback(
        () => dispatch(resetFromStep({ step: 'payment', order: stepOrder })),
        [dispatch, stepOrder]
    );

    return {
        updateServices,
        updateAddOns,
        dismissAutoAddOn: dismissAutoAddOnAction,
        applyAutoAddOnBaseline: applyAutoAddOnBaselineAction,
        setResolvedPlanId: setResolvedPlanIdAction,
        setServicePlanSessionId: setServicePlanSessionIdAction,
        setSmriqConfirmedVehicle: setSmriqConfirmedVehicleAction,
        syncSmriqConfirmedMileage: syncSmriqConfirmedMileageAction,
        setSmriqPlanSettings: setSmriqPlanSettingsAction,
        setScheduleOverrides: setScheduleOverridesAction,
        applyDerivedSchedule: applyDerivedScheduleAction,
        clearPaymentSelection: clearPaymentSelectionAction,
    };
};
